import { Search } from "lucide-react";
import { useId, useMemo, useState } from "react";

import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { shortHash } from "@/lib/hash-display";

import {
  formatAdded,
  formatCharge,
  matchesMoleculeFilter,
  moleculeOptionLabel,
  type MoleculeOption,
} from "./molecule-option";

export interface MoleculePickerProps {
  /** `id` of the `<select>`, so the caller's `<label htmlFor>` names it. */
  id: string;
  /** Newest first. The picker neither sorts nor dedupes. */
  options: readonly MoleculeOption[];
  /** The selected option's `id`, or undefined for none. */
  value: string | undefined;
  onChange: (id: string | undefined) => void;
  /** Text of the empty option, e.g. `— select a Molecule —`. */
  placeholder: string;
  /** Names the filter box for assistive tech, e.g. `Filter molecules for ligand`. */
  filterLabel: string;
  disabled?: boolean;
  /** Forwarded to the `<select>` (the port's description). */
  "aria-describedby"?: string;
}

/**
 * A Molecule picker a person can find a molecule in (flowstack#1402).
 *
 * It used to be a bare select of `sha256:… — uploaded` rows: nothing to
 * recognise a known ligand by, and no way to search, so re-uploading was the
 * easy path and the catalog filled with copies. Each option now leads with its
 * formula, atom count and charge, then where it came from and when, then the
 * digest. A filter box narrows the list over all of those plus the SMILES and
 * InChIKey, and the chosen molecule is shown in full underneath.
 *
 * **Still a native `<select>`.** The keyboard, screen-reader and mobile
 * behaviour of a native control come for free, and the submit pages' existing
 * label wiring keeps working. A native option is one line of text, which is
 * why the full detail lives in the summary under it rather than in the row.
 *
 * **The selection survives the filter.** A native select whose value has no
 * matching option shows blank while still holding the value, so the selected
 * option stays in the list whatever the filter says.
 *
 * Props only, no store or query coupling, so it can move to flowstack-ui.
 */
export function MoleculePicker({
  id,
  options,
  value,
  onChange,
  placeholder,
  filterLabel,
  disabled,
  "aria-describedby": describedBy,
}: MoleculePickerProps): JSX.Element {
  const [query, setQuery] = useState("");
  const countId = useId();

  const shown = useMemo(
    () => options.filter((o) => o.id === value || matchesMoleculeFilter(o, query)),
    [options, value, query]
  );
  const matchCount = shown.filter((o) => matchesMoleculeFilter(o, query)).length;
  const selected = options.find((o) => o.id === value);
  const filtering = query.trim() !== "";

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          aria-label={filterLabel}
          aria-controls={id}
          aria-describedby={countId}
          placeholder="Filter by formula, SMILES, InChIKey, run, date or digest"
          className="h-9 pl-9"
        />
      </div>
      <p id={countId} className="text-xs text-muted-foreground" aria-live="polite">
        {filtering
          ? `${matchCount} of ${options.length} molecules match`
          : `${options.length} ${options.length === 1 ? "molecule" : "molecules"}, newest first`}
      </p>
      <Select
        id={id}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
        className="w-full"
        aria-describedby={describedBy}
      >
        <option value="">{placeholder}</option>
        {shown.map((option) => (
          <option key={option.id} value={option.id}>
            {moleculeOptionLabel(option)}
          </option>
        ))}
      </Select>
      {filtering && matchCount === 0 && (
        <p className="text-xs text-muted-foreground">No molecule matches “{query.trim()}”.</p>
      )}
      {selected && <MoleculeSummaryCard option={selected} />}
    </div>
  );
}

/** The chosen molecule, in full: what the one-line option had to elide. */
function MoleculeSummaryCard({ option }: { option: MoleculeOption }): JSX.Element {
  return (
    <dl
      aria-label="Selected molecule"
      className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-md border border-border bg-muted/20 p-2.5 text-xs"
    >
      <dt className="text-muted-foreground">Formula</dt>
      <dd className="font-mono">
        {option.formula ?? "unknown"}
        {option.atomCount !== null && (
          <span className="text-muted-foreground"> · {option.atomCount} atoms</span>
        )}
        {option.formalCharge !== null && (
          <span className="text-muted-foreground">
            {" "}
            · charge {formatCharge(option.formalCharge)}
          </span>
        )}
      </dd>
      {option.smiles && (
        <>
          <dt className="text-muted-foreground">SMILES</dt>
          <dd className="break-all font-mono">{option.smiles}</dd>
        </>
      )}
      {option.inchiKey && (
        <>
          <dt className="text-muted-foreground">InChIKey</dt>
          <dd className="font-mono">{option.inchiKey}</dd>
        </>
      )}
      <dt className="text-muted-foreground">Added</dt>
      <dd>{formatAdded(option.createdAt)}</dd>
      <dt className="text-muted-foreground">From</dt>
      <dd>
        {option.producer === null ? (
          "uploaded"
        ) : (
          <>
            <span className="font-mono">{option.producer}</span>
            {option.runId !== null && (
              <>
                {" "}
                · run <span className="break-all font-mono">{option.runId}</span>
              </>
            )}
          </>
        )}
      </dd>
      <dt className="text-muted-foreground">Digest</dt>
      <dd className="font-mono text-muted-foreground" title={option.digest}>
        {shortHash(option.digest)}
      </dd>
    </dl>
  );
}
